import { FC, useEffect, useRef, useState } from 'react';
import SbEditable from 'storyblok-react';

import { Article } from '../types/Franchises';
import { ContentBlockArtists } from './ContentBlockArtists';
import { ContentBlockBannerList } from './ContentBlockBannerList';
import { ContentBlockFAQ } from './ContentBlockFAQ';
import { ContentBlockFramingContent } from './ContentBlockFramingContent';
import { ContentBlockFramingPreviewer } from './ContentBlockFramingPreviewer';
import { ContentBlockHighlight } from './ContentBlockHighlight';
import { ContentBlockImage } from './ContentBlockImage';
import { ContentBlockInsights } from './ContentBlockInsights';
import { ContentBlockInstagram } from './ContentBlockInstagram';
import { ContentBlockItemList } from './ContentBlockItemList';
import { ContentBlockProducts } from './ContentBlockProducts';
import { ContentBlockQuote } from './ContentBlockQuote';
import { ContentBlockSoundCloud } from './ContentBlockSoundCloud';
import { ContentBlockStickySubscription } from './ContentBlockStickySubscription';
import { ContentBlockSubscriptionReleases } from './ContentBlockSubscriptionReleases';
import { ContentBlockText } from './ContentBlockText';
import { ContentBlockTimeline } from './ContentBlockTimeline';
import { ContentBlockTitle } from './ContentBlockTitle';
import { ContentBlockTweet } from './ContentBlockTweet';
import { ContentBlockVideo } from './ContentBlockVideo';

interface ContentBlocksProps {
  content: Article;
}

export const ContentBlocks: FC<ContentBlocksProps> = ({ content = [] }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isSectionsInView, setIsSectionsInView] = useState(false);

  const stickyScrollTriggerId = (content || []).find(
    (s) =>
      s.component === 'productList' || s.component === 'stickySubscriptionCTA',
  )?._uid;

  useEffect(() => {
    const el = ref.current;
    if (!stickyScrollTriggerId || !el) return undefined;
    const triggerFromTop = 240;
    const handleScroll = () => {
      const { bottom } = el.getBoundingClientRect();
      const vh = window.innerHeight;
      setIsSectionsInView(window.scrollY > triggerFromTop && bottom > vh / 2);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [stickyScrollTriggerId]);

  const component = (blok: Article[0]) => {
    switch (blok.component) {
      case 'richText':
        return <ContentBlockText blok={blok} />;
      case 'highlightText':
        return <ContentBlockHighlight blok={blok} />;
      case 'quote':
        return <ContentBlockQuote blok={blok} />;
      case 'multiImage':
        return <ContentBlockImage blok={blok} />;
      case 'inlineVideo':
        return <ContentBlockVideo blok={blok} />;
      case 'itemList':
        return <ContentBlockItemList blok={blok} />;
      case 'bannerList':
        return <ContentBlockBannerList blok={blok} />;
      case 'artistList':
        return <ContentBlockArtists blok={blok} />;
      case 'productList':
        return <ContentBlockProducts blok={blok} />;
      case 'insightList':
        return <ContentBlockInsights blok={blok} />;
      case 'soundCloud':
        return <ContentBlockSoundCloud blok={blok} />;
      case 'twitter':
        return <ContentBlockTweet blok={blok} />;
      case 'instagram':
        return <ContentBlockInstagram blok={blok} />;
      case 'releaseSubscriptionCTA':
        return <ContentBlockSubscriptionReleases blok={blok} />;
      case 'stickySubscriptionCTA':
        return (
          <ContentBlockStickySubscription
            blok={blok}
            isSectionsInView={isSectionsInView}
          />
        );
      case 'faqList':
        return <ContentBlockFAQ blok={blok} />;
      case 'timelineList':
        return <ContentBlockTimeline blok={blok} />;
      case 'title':
        return <ContentBlockTitle blok={blok} />;
      case 'framingPreviewer':
        return <ContentBlockFramingPreviewer blok={blok} />;
      case 'framingContent':
        return <ContentBlockFramingContent blok={blok} />;
      default:
        return null;
    }
  };

  return (
    <article ref={ref} className="flex flex-col gap-y-9 md:gap-y-12">
      {content.map((blok: Article[0]) => (
        <div
          key={blok._uid}
          className="scroll-mt-24 md:scroll-mt-28"
          id={blok._uid.substring(0, 8)}
        >
          {/* @ts-expect-error require storyblok-react upgrade */}
          <SbEditable content={blok}>{component(blok)}</SbEditable>
        </div>
      ))}
    </article>
  );
};
